import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HomeSuitabilitySection = () => {
  const suitabilityFactors = [
    {
      icon: 'Home',
      title: 'Isolatieniveau',
      description: 'Uw woning moet goed geïsoleerd zijn voor optimale prestaties.',
      requirements: [
        'Minimaal dakisolatie (Rc 3.5 of hoger)',
        'Spouwmuur- of gevelisolatie aanwezig',
        'HR++ beglazing of beter'
      ],
      impact: 'Kritiek',
      color: 'red'
    },
    {
      icon: 'Thermometer',
      title: 'Afgiftesysteem',
      description: 'Het type verwarming in uw woning bepaalt welke warmtepomp geschikt is.',
      requirements: [
        'Vloerverwarming: ideaal voor all-electric',
        'Lagetemperatuur radiatoren: geschikt',
        'Oude radiatoren: hybride warmtepomp aanbevolen'
      ],
      impact: 'Belangrijk',
      color: 'orange'
    },
    {
      icon: 'Zap',
      title: 'Elektrische aansluiting',
      description: 'Controleer of uw meterkast voldoende capaciteit heeft.',
      requirements: [
        'Minimaal 3x25A aansluiting',
        'Mogelijk verzwaring nodig',
        'Aparte groep voor warmtepomp'
      ],
      impact: 'Belangrijk',
      color: 'orange'
    },
    {
      icon: 'MapPin',
      title: 'Ruimte voor buitenunit',
      description: 'De buitenunit heeft voldoende ruimte en ventilatie nodig.',
      requirements: [
        'Minimaal 50cm vrije ruimte rondom',
        'Niet direct tegen slaapkamerraam',
        'Stevige ondergrond of muurbevestiging'
      ],
      impact: 'Gemiddeld',
      color: 'blue'
    }
  ];

  const impactColors = {
    red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Is uw woning geschikt?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Controleer deze belangrijke factoren om te bepalen of een warmtepomp geschikt is voor uw situatie.
          </p>
        </motion.div>

        {/* Suitability Factors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {suitabilityFactors?.map((factor, index) => {
            const colors = impactColors?.[factor?.color];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className={`bg-card border-2 ${colors?.border} rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-3 transition-all`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 ${colors?.bg} rounded-full flex items-center justify-center`}>
                    <Icon name={factor?.icon} size={28} className={colors?.icon} />
                  </div>
                  <span className={`px-3 py-1 ${colors?.badge} rounded-full text-xs font-semibold`}>
                    {factor?.impact}
                  </span>
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                  {factor?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {factor?.description}
                </p>
                <div className="space-y-2">
                  {factor?.requirements?.map((req, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className={`${colors?.icon} mt-0.5 flex-shrink-0`} />
                      <p className="text-sm text-muted-foreground">{req}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Suitability Check CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 lg:p-12 text-white text-center"
        >
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="ClipboardCheck" size={32} className="text-white" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
              Gratis geschiktheidscheck
            </h3>
            <p className="text-lg text-blue-100 mb-6">
              Laat een expert uw woning beoordelen en ontvang een persoonlijk advies over de beste warmtepomp voor uw situatie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" iconName="Calendar" iconPosition="left">
                Plan gratis adviesgesprek
              </Button>
              <Button size="lg" variant="outline" iconName="FileText" iconPosition="left" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                Offerte aanvragen
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeSuitabilitySection;